'use client';
import { Header } from '@/ui-components/organisms/Header/Header';
import { Grid } from '@/ui-components/atoms/Layout/Grid/Grid';
import { Text } from '@/ui-components/atoms/Text/Text';
import { StyledContainer } from '@/ui-components/atoms/Layout/StyledContainer/StyledContainer';
import { RenderRTE } from '@/components-2.0/structure/RenderRTE';
import { FlexibleFormVariant } from './FlexibleFormBase';
import { FlexibleFormVariantFactoryArgs, leftAlignedHeaderStyles, renderNarrowFlexibleFormContent } from './flexibleFormVariantHelpers';
import styles from './flexibleForm.module.scss';

const {
    descriptionTextStyles,
    flexForm,
    halfWidth,
    fullWidth,
    formSectionHeaderStyles,
    formSectionTextBlockStyles,
    submitButtonWrapperCentered,
    dropdownSelectStyles,
    submitButtonWrapperRight,
    wrapperRow,
    stateContainer,
    stateWrapper,
    zipInputStyles,
    selectorStyles,
    duplicationFormError,
    ctaCaption,
    promoCodeEntryShell,
    promoCodeEntryInner,
    promoCodeEntryAlert,
    promoCodeEntryForm,
    promoCodeEntryInput,
} = styles;

export function createPromoCodeEntryVariant({
    asset,
    nestedForm,
    toggleForm,
    hasHeadlineContent,
    alignment,
}: FlexibleFormVariantFactoryArgs): Pick<FlexibleFormVariant, 'styles' | 'submitButtonFitting' | 'renderContent'> {
    const promoFormClassName = `${flexForm} ${promoCodeEntryForm}`;

    return {
        styles: {
            flexForm,
            halfWidth,
            fullWidth,
            formSectionHeaderStyles,
            formSectionTextBlockStyles,
            inputStyles: alignment === 'Narrow' ? `${promoCodeEntryInput} ${styles.narrowInput}` : promoCodeEntryInput,
            dropdownSelectStyles,
            wrapperRow,
            stateContainer,
            stateWrapper,
            zipInputStyles,
            selectorStyles,
        },
        submitButtonFitting: 'fill-container',
        renderContent: (contentArgs) => {
            const promoAlertMessage =
                contentArgs.submitState !== 'duplicate' && contentArgs.validations.form?.validations.formerror
                    ? contentArgs.validations.form.helperText.formerror
                    : null;
            // The promo input's aria-describedby points at this id.
            const promoAlert = promoAlertMessage ? (
                <div id={contentArgs.formAlertId} className={promoCodeEntryAlert}>
                    {promoAlertMessage}
                </div>
            ) : null;

            if (alignment === 'Narrow') {
                return renderNarrowFlexibleFormContent(
                    { asset, nestedForm, toggleForm, hasHeadlineContent, alignment, formClassName: promoFormClassName },
                    { ...contentArgs, errorDisplay: promoAlert },
                );
            }

            if (alignment === 'Centre') {
                return (
                    <>
                        {hasHeadlineContent && !toggleForm && (
                            <StyledContainer standardMoleculeSpacing>
                                <Header
                                    headingTag="h2"
                                    heading={<RenderRTE contentfulDoc={asset.fields.headline} />}
                                    subhead={<RenderRTE contentfulDoc={asset.fields.description} />}
                                />
                            </StyledContainer>
                        )}
                        <Grid container noPadding={nestedForm}>
                            <Grid
                                col={nestedForm ? { sm: 12 } : { sm: 12, md: 6, lg: 6 }}
                                start={nestedForm ? { sm: 1 } : { md: 4, lg: 4 }}
                            >
                                {promoAlert}
                            </Grid>
                            <Grid
                                col={nestedForm ? { sm: 12 } : { sm: 12, md: 6, lg: 6 }}
                                start={nestedForm ? { sm: 1 } : { md: 4, lg: 4 }}
                            >
                                <div>
                                    <div className={promoFormClassName}>{contentArgs.wrappedFieldElements}</div>
                                    <div className={submitButtonWrapperCentered}>
                                        {contentArgs.duplicateError && (
                                            <StyledContainer>
                                                <Text tag="div" className={duplicationFormError} role="alert">
                                                    {contentArgs.duplicateErrorMessage}
                                                </Text>
                                            </StyledContainer>
                                        )}
                                        {contentArgs.submitButton}
                                        {asset.fields.cta_caption && (
                                            <RenderRTE contentfulDoc={asset.fields.cta_caption} className={ctaCaption} />
                                        )}
                                    </div>
                                </div>
                            </Grid>
                        </Grid>
                    </>
                );
            }

            return (
                <Grid container>
                    {hasHeadlineContent && !toggleForm && (
                        <Grid col={{ sm: 12, md: 6 }} standardMoleculeSpacing>
                            <Header
                                headingTag="h2"
                                heading={<RenderRTE contentfulDoc={asset.fields.headline} />}
                                theme="left"
                                className={leftAlignedHeaderStyles}
                            />
                        </Grid>
                    )}
                    <Grid col={{ sm: 12, md: 12 }}>
                        <div className={promoCodeEntryShell}>
                            <Text tag="p" className={descriptionTextStyles}>
                                <RenderRTE contentfulDoc={asset.fields.description} />
                            </Text>
                            <Grid col={{ sm: 12 }} className={promoCodeEntryInner}>
                                {promoAlert}
                                <div className={promoFormClassName}>
                                    {contentArgs.wrappedFieldElements}
                                    <div className={submitButtonWrapperRight}>
                                        {contentArgs.duplicateError && (
                                            <StyledContainer>
                                                <Text tag="div" className={duplicationFormError} role="alert">
                                                    {contentArgs.duplicateErrorMessage}
                                                </Text>
                                            </StyledContainer>
                                        )}
                                        {contentArgs.submitButton}
                                        {asset.fields.cta_caption && (
                                            <RenderRTE contentfulDoc={asset.fields.cta_caption} className={ctaCaption} />
                                        )}
                                    </div>
                                </div>
                            </Grid>
                        </div>
                    </Grid>
                </Grid>
            );
        },
    };
}
